/* eslint-disable complexity */

import React from 'react';
import cx from 'classnames';
import { useTranslations } from '@unisporkal/localization';
import { useSiteMap } from '@unisporkal/sites';
import { useTheme } from '@unisporkal/themes';
import { RawHtml } from '@unisporkal/utilities';
import AssetRestrictions from '../AssetRestrictions/AssetRestrictions';

import useSelectedIndex from '../../../hooks/stateful/useSelectedIndex.ts';
import useOnLoad from '../../../hooks/queries/useOnLoad';
import styles from './UsageRestrictionsWarning.module.scss';

const UsageRestrictionsWarning = () => {
  const t = useTranslations();
  const siteMap = useSiteMap();
  const theme = useTheme();
  const themeLink = theme.link || {};
  const { selectedOption } = useSelectedIndex();
  const { response: { config: { options, restrictions = [] } = {} } = {} } =
    useOnLoad();
  const imagePack = selectedOption?.type === 'ImagePack';
  const easyAccess = selectedOption?.type === 'EasyAccess';
  const premiumAccess = selectedOption?.type === 'PremiumAccess';

  const showRestrictions = () => {
    if (restrictions?.length === 0) {
      return false;
    }
    const hideEzaAgreementCheck = localStorage.getItem(
      'downloadPreferences.hideEzaAgreement'
    );
    const hideModifiedEzaAgreementCheck = localStorage.getItem(
      'downloadPreferences.hideModifiedEzaAgreement'
    );
    const hideEzaMessagingCheck = localStorage.getItem(
      'downloadPreferences.hideEzaMessaging'
    );
    const hideModifiedEzaMessagingCheck = localStorage.getItem(
      'downloadPreferences.hideModifiedEzaMessaging'
    );
    const alwaysDownloadLargestCheck = localStorage.getItem(
      'downloadPreferences.alwaysDownloadLargest'
    );
    const hideEzaAgreement =
      hideEzaAgreementCheck === 'true' ||
      hideEzaAgreementCheck === true ||
      hideModifiedEzaAgreementCheck === 'true' ||
      hideModifiedEzaAgreementCheck === true;
    const hideEzaMessaging =
      hideEzaMessagingCheck === 'true' ||
      hideEzaMessagingCheck === true ||
      hideModifiedEzaMessagingCheck === 'true' ||
      hideModifiedEzaMessagingCheck === true;
    const alwaysDownloadLargest =
      alwaysDownloadLargestCheck === 'true' ||
      alwaysDownloadLargestCheck === true;
    const paWithRequiredFields =
      premiumAccess &&
      (selectedOption.projectCodeRequired || selectedOption.notesRequired);
    const isLargeVectorImage =
      selectedOption.sizes[selectedOption.sizes.length - 1].name === 'eps';

    return (
      !alwaysDownloadLargest ||
      imagePack ||
      options?.length > 1 ||
      (easyAccess && !hideEzaAgreement) ||
      paWithRequiredFields ||
      isLargeVectorImage ||
      (easyAccess && !hideEzaMessaging)
    );
  };

  const renderAssetRestrictionText = () => (
    <section className={cx(styles.restrictionsList, themeLink.textLink)}>
      {imagePack && (
        <section className={styles.packRestrictions}>
          <RawHtml>
            {t('packs_editorial_restriction', {
              links: [
                {
                  href: `${siteMap.licenseAgreement()}#RF`,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                },
                {
                  href: `${siteMap.licenseAgreement()}#RM`,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                },
              ],
            })}
          </RawHtml>
        </section>
      )}
      <AssetRestrictions restrictions={restrictions} />
    </section>
  );

  if (!selectedOption || !(selectedOption.sizes?.length > 0)) {
    return null;
  }
  if (!showRestrictions()) {
    return null;
  }

  return (
    <section
      className={styles.container}
      data-testid="usage-restrictions-warning"
    >
      <div className={styles.title}>{t('usage_rights_restrictions')}</div>
      {renderAssetRestrictionText()}
    </section>
  );
};

export default UsageRestrictionsWarning;
